<script setup lang="ts">
import { computed, defineComponent, h, ref, watch } from 'vue'
import { defineAsyncWithSkeleton } from '~/utils/defineAsyncWithSkeleton'

const AvatarComposerFallback = defineComponent({
  name: 'UserAvatarComposerFallback',
  setup() {
    return () => h('span', {
      class: 'user-avatar__composer-fallback',
      'aria-hidden': 'true',
      style: {
        display: 'block',
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        background: 'var(--color-bg-muted)',
      },
    })
  },
})

const AvatarComposer = defineAsyncWithSkeleton(
  function loadAvatarComposer() {
    return import('~/components/orga/avatar/AvatarComposer.vue')
  },
  AvatarComposerFallback,
)

const props = withDefaults(
  defineProps<{
    fullName: string
    email: string
    variant?: 'default' | 'panel'
    nni?: string
    displayMode?: 'teams' | 'initials' | 'avatar'
    avatarConfig?: Record<string, unknown> | null
    photoUrl?: string | null
  }>(),
  {
    variant: 'default',
    nni: undefined,
    displayMode: 'teams',
    avatarConfig: null,
    photoUrl: null,
  }
)

const showPhoto = ref(true)

const initials = computed(() => {
  const parts = props.fullName.trim().split(' ')
  return parts.map((part) => part[0]).slice(0, 2).join('').toUpperCase()
})

const rootClass = computed(() => ({
  'user-avatar': true,
  'user-avatar--panel': props.variant === 'panel',
}))

watch(
  () => props.photoUrl,
  () => {
    showPhoto.value = true
  }
)

function onPhotoError(): void {
  showPhoto.value = false
}

function onPhotoLoad(event: Event): void {
  const image = event.target
  if (!(image instanceof HTMLImageElement) || image.naturalWidth === 0) {
    showPhoto.value = false
  }
}
</script>

<template>
  <span :class="rootClass">
    <AvatarComposer
      v-if="displayMode === 'avatar' && avatarConfig"
      class="user-avatar__composer"
      :config="avatarConfig as any"
    />
    <img
      v-else-if="displayMode === 'teams' && photoUrl && showPhoto"
      :src="photoUrl"
      alt=""
      @error="onPhotoError"
      @load="onPhotoLoad"
    >
    <span
      v-else
      class="avatar-fallback"
    >
      {{ initials }}
    </span>
  </span>
</template>

<style lang="scss">
.user-avatar {
  width: 40px;
  height: 40px;
  border: 1px solid var(--color-border-subtle);
  border-radius: 50%;
  box-shadow: 0 1px 6px 0 var(--color-shadow-soft);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-bg-elevated);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .user-avatar__composer {
    width: 100%;
    height: 100%;
  }

  .avatar-fallback {
    margin: 0;
    display: flex;
    align-items: center;
    line-height: 1;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-weight: bold;
    font-size: 1rem;
    color: var(--color-primary);
  }
}

.user-avatar--panel {
  width: 52px;
  height: 52px;
  border: none;
  background-color: var(--color-primary);
  box-shadow: 0 2px 8px 1px rgba(16, 16, 20, 0.16); /* elevation-100 */

  .avatar-fallback {
    color: var(--color-text-on-primary, #fff);
    font-size: 1.125rem;
  }
}
</style>
